// JSON-LD Schema generators for SEO structured data

const SITE_URL = "https://willtheyconvert.com";
const SITE_NAME = "WillTheyConvert";
const LOGO_URL = `${SITE_URL}/wtc-logo.png`;

export interface FAQItem {
  question: string;
  answer: string;
}

export interface HowToStep {
  name: string;
  text: string;
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function getOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: LOGO_URL,
    sameAs: ["https://twitter.com/willtheyconvert"],
    description: "Test if people will pay for your product idea before building it. Validate startup ideas with fake door testing.",
  };
}

export function getWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    description: "Validate your product ideas before building. Create fake door tests to measure real market interest.",
  };
}

export function getSoftwareApplicationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE_NAME,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: "Create validation tests to measure real purchase intent before building your product.",
    offers: {
      "@type": "Offer",
      price: "19",
      priceCurrency: "USD",
      description: "Lifetime access",
    },
  };
}

export function getFAQPageSchema(faqs: FAQItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function getHowToSchema(name: string, description: string, steps: HowToStep[]) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description,
    step: steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  };
}

export function getBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
    })),
  };
}

// Pre-built FAQ data from the homepage FAQ section
export const homepageFAQs: FAQItem[] = [
  {
    question: "What exactly are validation tests?",
    answer: "You create what looks like a real product page with real \"Buy\" buttons. When people interact with it, you learn if they'd actually pay for your product - not just say they would. It's the same method Dropbox used to get 75,000 signups before writing code, and Buffer used to get paying customers in 7 weeks. Completely legal, completely ethical, completely smart.",
  },
  {
    question: "What types of products can I validate?",
    answer: "Anything people might pay for online. SaaS apps, courses, newsletters, coaching, digital products, subscriptions, marketplaces. If it has a \"Buy\" button, you can test if people will click it.",
  },
  {
    question: "How is this different from lying to customers?",
    answer: "You're not lying - you're researching. Just like A/B testing or surveys, but more honest. You immediately tell interested users the product is in development and collect their email to notify them when it launches. They get early access, you get validation. Everyone wins.",
  },
  {
    question: "What data do I actually get?",
    answer: "The only metrics that matter: How many people saw your offer, how many clicked \"Buy\", where they dropped off, and their contact info if they're interested. No vanity metrics. Just \"will they convert?\"",
  },
  {
    question: "How long should I run my test?",
    answer: "Until you have an answer. Usually 48-72 hours with 50-100 visitors gives you enough data to know if you're onto something or wasting your time. Better to know in 3 days than 3 months.",
  },
  {
    question: "Can I make it match my brand?",
    answer: "Yes. Use your colors, your logo, your copy. It should look exactly like the real product page you'd build. The more realistic, the better the data.",
  },
  {
    question: "What happens to interested users?",
    answer: "You get their emails. They get early access when you build. Turn your validation test into your first customers. That's how smart founders launch.",
  },
  {
    question: "How quickly can I start?",
    answer: "Right now. Pick a template, add your details, share the link. You could have validation data before dinner. What are you waiting for?",
  },
];

// Pre-built HowTo steps from the homepage
export const homepageHowToSteps: HowToStep[] = [
  {
    name: "Create Your Flow",
    text: "Set up your business details, product info, and pricing in minutes. No coding required.",
  },
  {
    name: "Share Your Link",
    text: "Get a unique link to share with your target audience via social media, ads, or email.",
  },
  {
    name: "Track Real Intent",
    text: "Watch as real users interact with your offer. See who clicks 'Buy' and measure true purchase intent.",
  },
  {
    name: "Make Data-Driven Decisions",
    text: "Use real conversion data to decide whether to build, pivot, or move on to your next idea.",
  },
];
